import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useState } from "react";
import { useAccount } from "~/hooks/useAccount";
import { IPlan } from "~/types/billing";
import { usePayment } from "./usePayment";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetupIntent } from "@stripe/stripe-js";
import { toast } from "~/components/toast";
import { INVOICES, PLANS } from "~/api/billing/types";
import { STORE } from "~/api/store/types";
import { paths } from "~/router/paths";
import _debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { appendLocaleToSearchParams } from "~/translations/i18n";
import { set } from "lodash";
import axios from "axios";
import { useSendFailureReason } from "~/api/billing/queries";
import { triggerBingAdsTrialStart } from "~/helpers/uet";
import { gtmPushEcommerce } from "~/helpers/gtag";
import { getTotalPlanCost } from "~/helpers/numbers";
import { getCheckoutPaymentErrorMessage } from "./paymentErrorMessage";
import { useStatsigClient } from "@statsig/react-bindings";
import { markRecentPayment } from "~/helpers/plans";

// Generous enough to cover a slow webhook round-trip, short enough that a stuck
// customer gets told what to do rather than waiting forever.
const CONFIRMATION_TIMEOUT_MS = 45000;

export const useStripeConfirmation = (
  plan: IPlan | undefined,
  upgradeWithStore: boolean,
  setIsLoading?: (val: boolean) => void,
  onSuccess?: () => void,
  disable_redirect_on_finish?: boolean
) => {
  const [searchParams] = useSearchParams();

  // Either intent type can come back from Stripe's redirect. The second clause here
  // used to repeat setup_intent_client_secret, so this never reflected a
  // payment_intent return at all.
  const [isConfirming, setIsConfirming] = useState(
    (searchParams.has("setup_intent_client_secret") ||
      searchParams.has("payment_intent_client_secret")) &&
      searchParams.get("redirect_status") === "succeeded"
  );

  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { refetch, getAccount } = useAccount();
  const { logEvent } = useStatsigClient();
  const { payStripe } = usePayment({
    planId: plan?.id as string,
    isUserShopify: false,
    upgradeWithStore: upgradeWithStore,
  });
  const client = useQueryClient();
  const navigate = useNavigate();
  const sendFailureReasonMutation = useSendFailureReason();

  const navigatePreservingLocale = useCallback(
    (path: string) => {
      const locale = i18n.resolvedLanguage || i18n.language;
      navigate(path);
      if (locale) {
        void i18n.changeLanguage(locale);
      }
    },
    [i18n, navigate]
  );

  // Shown whenever a payment we believe succeeded cannot be confirmed. The customer
  // has already been charged at this point, so they get a way forward rather than a
  // dead end.
  const reportUnconfirmedPayment = useCallback(() => {
    toast.error(t("checkout.confirmation_timeout"), {
      inlineAction: {
        label: String(t("checkout.refresh_page")),
        onClick: () => window.location.reload(),
      },
    });
  }, [t]);

  // Last-resort ceiling on the post-payment spinner. Whatever goes wrong in the
  // confirmation path — a rejected Stripe promise, a request that never returns, a
  // plan that never loads — the customer must end up with an actionable message
  // rather than a spinner they sit on indefinitely after being charged.
  useEffect(() => {
    if (!isConfirming) return;

    const timer = setTimeout(() => {
      setIsConfirming(false);
      reportUnconfirmedPayment();
    }, CONFIRMATION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isConfirming, reportUnconfirmedPayment]);

  // The backend only reflects the new subscription once Stripe's webhook has
  // been processed, which can land seconds after confirmPayment resolves.
  // Poll until active_subscription appears so downstream guards don't treat a
  // paying user as unsubscribed and bounce them back to a pay state.
  // Returns false if it never appeared, so the caller can say so instead of
  // silently dropping the customer into the app unprovisioned.
  const waitForSubscriptionSync = async () => {
    const maxAttempts = 6;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const profile = await getAccount();
        if (profile?.active_subscription) return true;
      } catch {
        // transient fetch failure — keep retrying within the bounded loop
      }
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    return false;
  };

  const handleSuccess = async (plan: IPlan, transactionId?: string) => {
    try {
      logEvent("subscription_updated_fe", plan?.id, {
        plan_name: plan?.name,
        plan_price: String(plan?.cost_per_month),
      });
    } catch (error) {
      console.error("Statsig Log Error:", error);
    }

    client.invalidateQueries(PLANS);
    client.invalidateQueries(STORE);
    client.invalidateQueries(INVOICES);
    localStorage.removeItem("onboarding_with_create_store");
    markRecentPayment();

    // Payment succeeded, so we always let the customer through — being charged and
    // held at the door is the failure we are fixing here. But if provisioning has
    // not landed yet, say so rather than dropping them into the app silently.
    const isProvisioned = await waitForSubscriptionSync();
    if (!isProvisioned) {
      toast.warning(t("checkout.provisioning_delayed"));
    }

    onSuccess?.();

    // Trigger Bing Ads event for trial start
    triggerBingAdsTrialStart(plan);

    // GA4 purchase push for the Axon Pixel (via GTM).
    // shipping/tax are required by Axon's purchase schema; both 0 for SaaS.
    const value = getTotalPlanCost(plan);
    gtmPushEcommerce("purchase", {
      transaction_id: transactionId || String(plan.id ?? ""),
      currency: "USD",
      value,
      shipping: 0,
      tax: 0,
      items: [
        {
          item_id: String(plan.id ?? ""),
          item_name: `${plan.name} - ${plan.interval}`,
          price: value,
          quantity: 1,
        },
      ],
    });

    if (disable_redirect_on_finish) {
      return;
    }
    navigate(`${paths.app.home}?plan_id=${plan?.id}`);
  };

  const commitPayment = async (
    setupIntent: SetupIntent | null = null,
    promoCodeId?: string,
    returnError: boolean = false
  ) => {
    if (!plan) return;

    const error = await payStripe(
      setupIntent
        ? { setup_intent: setupIntent.id, promo_code_id: promoCodeId }
        : { promo_code_id: promoCodeId }
    );

    if (error) {
      const failureData =
        typeof error === "string"
          ? { reason: error }
          : {
              reason: error.message ?? String(t("checkout.went_wrong")),
              code: error.code,
              decline_code: error.decline_code,
              message: error.message,
            };
      sendFailureReasonMutation.mutate(failureData);
      if (
        error != "error" &&
        error.type === "invalid_request_error" &&
        error.code === "payment_intent_incompatible_payment_method"
      ) {
        refetch();
      }
      if (returnError) return { error: true };
    } else {
      handleSuccess(plan, setupIntent?.id);
    }
  };

  const debouncedSetupIntentCheck = useCallback(
    _debounce(
      async (setupIntentClientSecret: string, promoCodeId: string | null) => {
        if (!stripe || !plan) return;

        setIsConfirming(true);
        try {
          const { setupIntent, error } = await stripe.retrieveSetupIntent(
            setupIntentClientSecret
          );
          if (!setupIntent) {
            toast.warning(getCheckoutPaymentErrorMessage(error, t));
            return;
          }
          switch (setupIntent.status) {
            case "succeeded":
              await commitPayment(setupIntent, promoCodeId ?? undefined);
              break;
            case "processing":
              toast.warning(t("checkout.processing_payment"));
              break;
            case "requires_payment_method":
              toast.error(
                getCheckoutPaymentErrorMessage(
                  { code: "payment_method_required" },
                  t
                )
              );
              navigatePreservingLocale(window.location.pathname);
              break;
            default:
              toast.warning(t("checkout.went_wrong"));
              navigatePreservingLocale(window.location.pathname);
              break;
          }
        } catch (err) {
          // This chain had no rejection handler and cleared the spinner only on the
          // success branch, so any throw here stranded a paying customer on it.
          console.error("Error retrieving setup intent:", err);
          reportUnconfirmedPayment();
        } finally {
          setIsConfirming(false);
        }
      },
      500
    ),
    [stripe, plan, i18n, navigatePreservingLocale, t]
  );

  const debouncedPaymentIntentCheck = useCallback(
    _debounce(async (paymentIntentClientSecret: string) => {
      if (!stripe || !plan) return;

      setIsConfirming(true);
      try {
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret
        );
        if (!paymentIntent) {
          toast.warning(getCheckoutPaymentErrorMessage(error, t));
          return;
        }
        switch (paymentIntent.status) {
          case "succeeded":
            await handleSuccess(plan, paymentIntent.id);
            break;
          case "processing":
            toast.warning(t("checkout.processing_payment"));
            break;
          case "requires_payment_method":
            toast.error(
              getCheckoutPaymentErrorMessage(
                { code: "payment_method_required" },
                t
              )
            );
            navigatePreservingLocale(window.location.pathname);
            break;
          default:
            toast.warning(t("checkout.went_wrong"));
            navigatePreservingLocale(window.location.pathname);
            break;
        }
      } catch (err) {
        // Same missing rejection handler as the setup-intent path above.
        console.error("Error retrieving payment intent:", err);
        reportUnconfirmedPayment();
      } finally {
        setIsConfirming(false);
      }
    }, 500),
    [stripe, plan, navigatePreservingLocale, t]
  );

  useEffect(() => {
    const setupIntentClientSecret = searchParams.get(
      "setup_intent_client_secret"
    );
    const paymentIntentClientSecret = searchParams.get(
      "payment_intent_client_secret"
    );
    const promoCodeId = searchParams.get("promo_code_id");
    if (!stripe || !plan) return;
    if (setupIntentClientSecret && !paymentIntentClientSecret)
      debouncedSetupIntentCheck(setupIntentClientSecret, promoCodeId);
    if (paymentIntentClientSecret)
      debouncedPaymentIntentCheck(paymentIntentClientSecret);
  }, [stripe, plan]);

  const handlePayment = async (returnError = false) => {
    if (!stripe || !elements || !setIsLoading) {
      return;
    }

    setIsLoading(true);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("plan_id", plan?.id as string);
    appendLocaleToSearchParams(searchParams);

    let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: returnURL,
        },
      });
      if (error) {
        const failureData = {
          reason: error.message ?? String(t("checkout.went_wrong")),
          code: error.code,
          decline_code: error.decline_code,
          message: error.message,
        };
        sendFailureReasonMutation.mutate(failureData);
        toast.error(getCheckoutPaymentErrorMessage(error, t));
      }
      console.log("Error retrieving setup intent:", error);
    } catch (err: any) {
      const promoCodeId = searchParams.get("promo_code_id");
      const paymentError = await commitPayment(
        null,
        promoCodeId ?? undefined,
        returnError
      );
      if (returnError && paymentError?.error) return { error: true };
    }
    localStorage.removeItem("isOnboarding");
    setIsLoading(false);
  };
  return { handlePayment, isConfirming };
};
